import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import SimpleBar from 'simplebar-react';
import classNames from 'classnames';
import { 
  Home, Briefcase, DollarSign, TrendingUp, Users, 
  Package, Settings, BarChart2, Repeat, PieChart,
  AlertTriangle, Calendar, BarChart, Activity, Cpu,
  Grid, FileText, Search, Eye, Download, Upload,
  Link as LinkIcon, CreditCard, Save, User, Sliders,
  Lock, Bell, ChevronDown
} from 'react-feather';
import { useAppTheme, useIsMobile } from '@/context/AppThemeProvider';
import { useAuth } from '@/context/AuthProvider';
import { useUserProfile } from '@/hooks/useUserProfile';
import { PATHS } from '@/routes/paths';

interface MenuItem {
  key: string;
  label: string;
  icon?: React.ComponentType<any>;
  url?: string;
  children?: MenuItem[];
  isTitle?: boolean;
}

const menuItems: MenuItem[] = [
  { key: 'main', label: 'Main', isTitle: true },
  { key: 'dashboard', label: 'Dashboard', icon: Home, url: PATHS.dashboard },
  {
    key: 'portfolios',
    label: 'Portfolios',
    icon: Briefcase,
    children: [
      { key: 'holdings', label: 'Holdings', icon: BarChart2, url: PATHS.portfolio.holdings },
      { key: 'transactions', label: 'Transactions', icon: Repeat, url: PATHS.portfolio.transactions },
      { key: 'sectors', label: 'Sectors', icon: PieChart, url: PATHS.portfolio.sectors },
      { key: 'performance', label: 'Performance', icon: TrendingUp, url: PATHS.portfolio.performance },
      { key: 'risk', label: 'Risk Analysis', icon: AlertTriangle, url: PATHS.portfolio.risk }
    ]
  },
  {
    key: 'dividends',
    label: 'Dividends',
    icon: DollarSign,
    children: [
      { key: 'dividend-calendar', label: 'Calendar', icon: Calendar, url: PATHS.dividends.calendar },
      { key: 'dividend-performance', label: 'Performance', icon: BarChart, url: PATHS.dividends.performance },
      { key: 'income-lab', label: 'Income Lab', icon: Activity, url: PATHS.dividends.incomeLab }
    ]
  },
  {
    key: 'analytics',
    label: 'Analytics',
    icon: TrendingUp,
    children: [
      { key: 'ai-insights', label: 'AI Insights', icon: Cpu, url: PATHS.analytics.aiInsights },
      { key: 'widget-lab', label: 'Widget Lab', icon: Grid, url: PATHS.analytics.widgetLab },
      { key: 'reports', label: 'Custom Reports', icon: FileText, url: PATHS.analytics.reports }
    ]
  },
  {
    key: 'social',
    label: 'Social',
    icon: Users,
    children: [
      { key: 'find-users', label: 'Find Users', icon: Search, url: PATHS.social.findUsers },
      { key: 'following', label: 'Following', icon: Eye, url: PATHS.social.following },
      { key: 'groups', label: 'Investment Groups', icon: Users, url: PATHS.social.groups }
    ]
  },
  
  { key: 'tools', label: 'Tools & Settings', isTitle: true },
  {
    key: 'setup',
    label: 'Setup',
    icon: Package,
    children: [
      { key: 'import', label: 'Import Data', icon: Download, url: PATHS.setup.importData },
      { key: 'export', label: 'Export Data', icon: Upload, url: PATHS.setup.exportData },
      { key: 'link-broker', label: 'Link Brokerage', icon: LinkIcon, url: PATHS.setup.linkBroker },
      { key: 'link-bank', label: 'Link Bank', icon: CreditCard, url: PATHS.setup.linkBank },
      { key: 'backups', label: 'Backups', icon: Save, url: PATHS.setup.backups }
    ]
  },
  {
    key: 'settings',
    label: 'Settings',
    icon: Settings,
    children: [
      { key: 'profile', label: 'Profile', icon: User, url: PATHS.settings.profile },
      { key: 'preferences', label: 'Preferences', icon: Sliders, url: PATHS.settings.preferences },
      { key: 'subscription', label: 'Subscription', icon: CreditCard, url: PATHS.settings.subscription },
      { key: 'security', label: 'Security', icon: Lock, url: PATHS.settings.security },
      { key: 'notifications', label: 'Notifications', icon: Bell, url: PATHS.settings.notifications }
    ]
  }
];

export const AppSidebar: React.FC = () => {
  const location = useLocation();
  const { sidebarExpanded, setSidebarExpanded, sidebarVisible, setSidebarVisible } = useAppTheme();
  const { user } = useAuth();
  const { profile } = useUserProfile();
  const isMobile = useIsMobile();
  
  const [openMenus, setOpenMenus] = useState<string[]>([]);
  const [activeKey, setActiveKey] = useState<string>('');
  
  // When sidebar collapses, close all submenus
  useEffect(() => {
    if (!sidebarExpanded && !isMobile) {
      setOpenMenus([]);
    }
  }, [sidebarExpanded, isMobile]);

  // Also close menus when sidebar becomes not visible
  useEffect(() => {
    if (!sidebarVisible) {
      setOpenMenus([]);
    }
  }, [sidebarVisible]);

  const displayName = profile?.display_name || 
    (user?.user_metadata?.firstName && user?.user_metadata?.lastName 
      ? `${user.user_metadata.firstName} ${user.user_metadata.lastName}`
      : user?.user_metadata?.name || user?.email || "User");
  
  const userEmail = user?.email || "user@stonkfu.com";
  const avatarColor = profile?.avatar_color || '#00A8A8';
  const initials = displayName.substring(0, 2).toUpperCase();

  // Determine active menu item based on current path
  useEffect(() => {
    const currentPath = location.pathname;
    
    const findActive = (items: MenuItem[], parent?: string): { key: string; parents: string[] } | null => {
      for (const item of items) {
        if (item.url === currentPath) {
          return { key: item.key, parents: parent ? [parent] : [] };
        }
        if (item.children) {
          const result = findActive(item.children, item.key);
          if (result) {
            return result;
          }
        }
      }
      return null;
    };
    
    const result = findActive(menuItems);
    if (result) {
      setActiveKey(result.key);
      // Don't automatically open submenus or expand sidebar when navigating
      // Let the user control the sidebar state
    }
  }, [location.pathname]);

  const toggleSubmenu = (key: string) => {
    // If sidebar is condensed and we're opening a submenu, expand the sidebar
    if (!isMobile && !sidebarExpanded) {
      setSidebarExpanded(true);
    }
    
    setOpenMenus(prev => {
      if (prev.includes(key)) {
        return prev.filter(k => k !== key);
      } else {
        return [...prev, key];
      }
    });
  };

  const handleBackdropClick = () => {
    if (isMobile) {
      setSidebarVisible(false);
    }
  };

  const renderMenuItem = (item: MenuItem, depth: number = 0) => {
    if (item.isTitle) {
      return (
        <div key={item.key} className="menu-section-header">
          {item.label}
        </div>
      );
    }

    const hasChildren = item.children && item.children.length > 0;
    const isOpen = openMenus.includes(item.key);
    const isActive = activeKey === item.key;
    const isParentActive = item.children?.some(child => child.key === activeKey);
    const Icon = item.icon;
    const isSubmenuItem = depth > 0;

    if (item.url && !hasChildren) {
      const handleClick = () => {
        // If clicking a submenu item and the parent isn't open, open it
        if (isSubmenuItem && !isMobile) {
          const parentKey = menuItems.find(m => m.children?.some(c => c.key === item.key))?.key;
          if (parentKey && !openMenus.includes(parentKey)) {
            setOpenMenus(prev => [...prev, parentKey]);
            setSidebarExpanded(true);
          }
        }
        if (isMobile) {
          setSidebarVisible(false);
        }
      };

      return (
        <Link
          key={item.key}
          to={item.url}
          className={classNames(
            isSubmenuItem ? 'submenu-item' : 'menu-item',
            { 'active': isActive }
          )}
          onClick={handleClick}
        >
          {Icon && <Icon size={18} className={isSubmenuItem ? 'submenu-icon' : 'menu-icon'} />}
          <span className={isSubmenuItem ? 'submenu-label' : 'menu-label'}>
            {item.label}
          </span>
          {!sidebarExpanded && !isSubmenuItem && !isMobile && (
            <div className="menu-tooltip">{item.label}</div>
          )}
        </Link>
      );
    }

    return (
      <div key={item.key}>
        <div
          className={classNames(
            isSubmenuItem ? 'submenu-item' : 'menu-item',
            {
              'has-children': hasChildren,
              'open': isOpen,
              'active': isActive || isParentActive
            }
          )}
          onClick={() => hasChildren && toggleSubmenu(item.key)}
        >
          {Icon && <Icon size={18} className={isSubmenuItem ? 'submenu-icon' : 'menu-icon'} />}
          <span className={isSubmenuItem ? 'submenu-label' : 'menu-label'}>
            {item.label}
          </span>
          {hasChildren && (sidebarExpanded || isMobile) && (
            <ChevronDown size={16} className={classNames('menu-arrow', { 'rotated': isOpen })} />
          )}
          {!sidebarExpanded && !isSubmenuItem && !isMobile && (
            <div className="menu-tooltip">{item.label}</div>
          )}
        </div>
        
        {hasChildren && (
          <div className={classNames('submenu-container', { 'open': isOpen })}>
            {item.children?.map(child => renderMenuItem(child, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  return (
    <>
      {/* Mobile Backdrop */}
      {isMobile && sidebarVisible && (
        <div className="sidebar-backdrop" onClick={handleBackdropClick} />
      )}

      {/* Sidebar */}
      <aside
        className={classNames('app-sidebar', {
          'visible': sidebarVisible,
          'expanded': sidebarExpanded || isMobile,
          'mobile': isMobile
        })}
      >

        {/* Mobile User Section */}
        {isMobile && (
          <div className="mobile-user-section">
            <div className="mobile-user-header">
              <div className="user-avatar" style={{ backgroundColor: avatarColor }}>
                {initials}
              </div>
              <div className="mobile-user-info">
                <div className="mobile-user-name">{displayName}</div>
                <div className="mobile-user-email">{userEmail}</div>
              </div>
            </div>
          </div>
        )}

        {/* Menu Content */}
        <SimpleBar className="sidebar-content" style={{ maxHeight: 'calc(100vh - 70px)' }}>
          <div className="menu-container">
            {menuItems.map(item => renderMenuItem(item))}
          </div>
        </SimpleBar>
      </aside>
    </>
  );
};